/**
 * Admin Feature API module.
 * @module app/admin/api
 */

import Moment from 'moment'

import matches from 'lodash.matches'
import filter from 'lodash/filter'
import { getCurrentSession } from '../../lib/authentication';

/** Validates a string conforms to a pattern of exactly seven digits */
const validateAgencyCode = value => /\d{7}/gm.test(value)

/** Validates a string conforms to one of the generally accepted email address patterns */
const validateBrokerEmail = value => /[\w\.+]+@\w+\.\w+(\.\w+)?/gm.test(value)

/** Validates whether or not an email address from a list is present no more than once */
const validateIsUnique = ({ email }, spreadsheet) => filter(spreadsheet, matches({ email })).length === 1

/**
 * Validates values from a row object in a list against a set of validation functions
 * @param {object} row - The row object to validate
 * @param {string} row.agencyCode - The string that should represent a seven-digit agency code
 * @param {string} row.email - The string that should represent an email address
 * @param {array<object>} spreadsheet - The source array that row is a member of
 * @returns {object} A collection of booleans mapped to each validation test
 */
const validateSpreadsheetRow = (row, spreadsheet) => {
    const { agencyCode, email } = row
    const validAgencyCode = validateAgencyCode(agencyCode)
    const validEmail = validateBrokerEmail(email)
    const isUnique = validateIsUnique(row, spreadsheet)
    return { validAgencyCode, validEmail, isUnique }
}

export const parseSpreadsheet = Spreadsheet => data => {
    const spreadsheet = Spreadsheet.read(data)

    return spreadsheet.reduce((acc, [agencyCode, email = ''], idx, arr) => {
        // This is a horrible test for determining if the first spreadsheet row is _probably_
        // the spreadsheet header, and if so, don't return it as part of the spreadsheet data.
        if (idx === 0) {
            const { validAgencyCode, validEmail } = validateSpreadsheetRow({ agencyCode, email }, arr)
            if (!validAgencyCode && !validEmail) {
                return acc
            }
        }
        return acc.concat([{ agencyCode, email, row: (idx + 1) }])
    }, [])
}

export const validateSpreadsheet = spreadsheet => {
    return spreadsheet.reduce((acc, { agencyCode, email }, idx, arr) => {
        const { validAgencyCode, validEmail, isUnique } = validateSpreadsheetRow({ agencyCode, email }, arr)
        if (![validAgencyCode, validEmail, isUnique].every(value => value)) {
            acc[idx] = { agencyCode: validAgencyCode, email: validEmail, unique: isUnique }
        }
        return acc
    }, {})
}

export const uploadSpreadsheet = Storage => (spreadsheet, email) => {
    const name = `brokers-whitelist-file_${Moment().format('YYYY-MM-DD_HH-mm-SSS')}.json`
    const data = JSON.stringify({
        meta: { email },
        data: spreadsheet.map(({ agencyCode, email }) => ({ agencyCode, email }))
    })
    return getCurrentSession().then((result) => {
        const { accessToken } = result;
        return apigClient.whitelistPost({}, data, {
            headers: {
                Authorization: accessToken
            },
        });
    });
};

/**
 * @example
 *  import Api from './api'
 *  const api = Api({ Storage: new Storage(), Spreadsheet: new Spreadsheet() })
 *  // ...
 *  const spreadsheet = api.parseSpreadsheet()
 * @param {}
 */
export default ({ Spreadsheet }) => ({
    parseSpreadsheet: parseSpreadsheet(new Spreadsheet()),
    uploadSpreadsheet: uploadSpreadsheet(),
    validateSpreadsheet: validateSpreadsheet,
})
